import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Grid, Divider, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BusinessIcon from '@mui/icons-material/Business';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import axios from 'axios';
import { consultaRuc } from '../../utils/consultaPeru';

const esSuperAdmin = (() => {
  try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.es_superadmin === 1; }
  catch { return false; }
})();

const EMPTY = {
  empresa_id: '', razon_social: '', ruc: '', rmv: 1025, uit: 5350,
  cta_remuneraciones: '6211', cta_onp: '4031', cta_afp: '4032', cta_essalud: '4033',
  cta_cts_provision: '4115', cta_gratif_provision: '4111', cta_vacac_provision: '4112'
};

export default function EmpresasRRHH() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [buscandoRuc, setBuscandoRuc] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/rrhh/empresas');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleBuscarRuc = async () => {
    if (form.ruc.length !== 11) return;
    setBuscandoRuc(true);
    try {
      const d = await consultaRuc(form.ruc);
      if (d) {
        if (d.razonSocial) sf('razon_social', d.razonSocial);
        else if (d.nombre) sf('razon_social', d.nombre);
      } else {
        setError('RUC no encontrado en SUNAT');
      }
    } catch (_) {
      setError('Error al consultar SUNAT');
    } finally { setBuscandoRuc(false); }
  };

  const handleGuardar = async () => {
    setError('');
    try {
      if (editId) await axios.put(`/api/rrhh/empresas/${editId}`, form);
      else await axios.post('/api/rrhh/empresas', form);
      setOpen(false);
      setSuccess(editId ? 'Empresa actualizada.' : 'Empresa creada.');
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
  };

  const handleEditar = (r) => {
    setEditId(r.empresa_id);
    setForm({ ...EMPTY, ...r });
    setOpen(true);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1100 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <BusinessIcon sx={{ color: '#1976d2', fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#222' }}>Empresas RRHH</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {esSuperAdmin && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditId(null); setForm(EMPTY); setOpen(true); }}>
              Nueva Empresa RRHH
            </Button>
          </Box>
        )}

        <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>RUC</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>RMV</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>UIT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ctas. Contables</TableCell>
                    {esSuperAdmin && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay empresas de RR.HH. configuradas</TableCell></TableRow>
                  ) : rows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell><strong>{r.razon_social || '—'}</strong><br /><span style={{ fontSize: '0.8em', color: '#666' }}>ID empresa: {r.empresa_id}</span></TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{r.ruc || '—'}</TableCell>
                      <TableCell align="right">S/ {Number(r.rmv || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">S/ {Number(r.uit || 0).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: '0.75em', color: '#555' }}>
                        Rem: {r.cta_remuneraciones} | AFP/ONP: {r.cta_afp}/{r.cta_onp} | EsSalud: {r.cta_essalud}
                      </TableCell>
                      {esSuperAdmin && (
                        <TableCell align="center">
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEditar(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight="bold">{editId ? 'Editar Empresa RRHH' : 'Nueva Empresa RRHH'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="ID Empresa (cont_empresa.id)" size="small" type="number" value={form.empresa_id} onChange={e => sf('empresa_id', e.target.value)} disabled={!!editId} sx={{ minWidth: '16em' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="RUC" size="small" value={form.ruc}
                onChange={e => sf('ruc', e.target.value.replace(/\D/g, ''))}
                sx={{ minWidth: '16em' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Consultar RUC en SUNAT">
                        <span>
                          <IconButton size="small" onClick={handleBuscarRuc}
                            disabled={buscandoRuc || form.ruc.length !== 11}>
                            {buscandoRuc ? <CircularProgress size={16} /> : <TravelExploreIcon fontSize="small" color={form.ruc.length === 11 ? 'primary' : 'disabled'} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  )
                }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Razón Social" size="small" value={form.razon_social} onChange={e => sf('razon_social', e.target.value)} sx={{ minWidth: '16em' }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="RMV vigente (S/)" size="small" type="number" value={form.rmv} onChange={e => sf('rmv', e.target.value)} sx={{ minWidth: '10em' }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="UIT vigente (S/)" size="small" type="number" value={form.uit} onChange={e => sf('uit', e.target.value)} sx={{ minWidth: '10em' }} />
            </Grid>
            <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">CUENTAS CONTABLES (ASIENTOS AUTOMÁTICOS)</Typography></Divider></Grid>
            {[
              ['cta_remuneraciones','Remuneraciones'],['cta_onp','ONP'],['cta_afp','AFP'],
              ['cta_essalud','EsSalud'],['cta_cts_provision','Provisión CTS'],
              ['cta_gratif_provision','Prov. Gratificaciones'],['cta_vacac_provision','Prov. Vacaciones']
            ].map(([k, label]) => (
              <Grid item xs={6} sm={4} key={k}>
                <TextField fullWidth label={label} size="small" value={form[k] || ''} onChange={e => sf(k, e.target.value)} sx={{ minWidth: '10em' }} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar}>{editId ? 'Actualizar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
